import { useState } from 'react';
import {
  Chip,
  IconButton,
  InputAdornment,
  Tooltip,
  createFilterOptions,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import {
  useCreateItemTagMutation,
  useCreateRoleMutation,
  useCreateTagMutation,
  useDeleteItemTagMutation,
  useGetItemTagsQuery,
  useGetOrganizationsQuery,
  useGetRolesQuery,
  useGetSubscribersQuery,
  useGetSubscriptionContactsQuery,
  useGetTagsQuery,
} from 'store/api';
import { getIn, FormikHelpers } from 'formik';
import {
  sortString,
  JItem,
  AvatarsCell,
  NoMaxWidthTooltip,
} from '@centerpiece/components';
import {
  Organization,
  Role,
  Subscriber,
  SubscriptionContact,
} from 'store/api/models';
import {
  internalRoleHolderOptions,
  stakeHolderOptions,
} from 'utils/contacts.utils';
import { renderStakeholderContactGroups } from './useProjectForm';
import { ContactTooltip } from 'components/ContactTooltip';

export const useStakeholderForm = (
  setOpenContact: Function,
  arrayName?: string
): JItem & {
  roles: Role[];
  subscription_contacts: SubscriptionContact[];
  organizations: Organization[];
} => {
  const { data: roles = [] } = useGetRolesQuery({});
  const [createRole] = useCreateRoleMutation();
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });

  return {
    roles,
    subscription_contacts,
    organizations,
    name: arrayName || 'stakeholders',
    itemType: 'fieldarray',
    label: 'Stakeholder',
    gridProps: {
      // sx: { paddingTop: '10px !important' },
    },
    array: [
      {
        precondition: (values: any) =>
          !values?.[arrayName || 'stakeholders']?.[values.index]
            ?.stakeholdertype,
        name: 'stakeholdertype',
        label: 'Type',
        itemType: 'autocomplete',
        // freeSolo: true, //REMOVING THE ABILITY TO ADD
        jItemProps: {
          // freeSolo: true,
          // addValue: async (
          //   name: any,
          //   newValue: any,
          //   values: any,
          //   formik: FormikHelpers<any>
          // ) => {
          //   const res = await createRole({
          //     name: newValue?.inputValue,
          //     type: 'External',
          //   }).unwrap();
          //   setTimeout(function () {
          //     formik.setFieldValue(name, res);
          //   }, 500);
          // },
        },
        options: [
          ...[...(roles.filter(role => role.type === 'External') || [])].sort(
            (a, b) => sortString(a.name, b.name)
          ),
          // { id: -1, name: 'Custom' },
        ],
      },
      ...(roles
        ?.filter(r => r.type === 'External')
        .map(role =>
          variableRoleStakeholder(
            role,
            stakeHolderOptions({
              subscription_contacts,
              organizations,
              role,
            }),
            setOpenContact,
            arrayName
          )
        ) || []),
      customStakeholder(
        stakeHolderOptions({
          subscription_contacts,
          organizations,
        }),
        setOpenContact,
        arrayName
      ),
    ],
  };
};

export const useInternalRolesForm = (
  setOpenContact: Function,
  arrayName?: string
): JItem & {
  roles: Role[];
  subscribers: Subscriber[];
} => {
  const { data: roles = [] } = useGetRolesQuery({});
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const sortedRoles: Role[] = [
    ...[...(roles.filter(role => role.type === 'Internal') || [])].sort(
      (a, b) => sortString(a.name, b.name)
    ),
    { id: 0, name: 'No role' } as Role,
  ];
  return {
    roles: sortedRoles,
    subscribers,
    name: arrayName || 'contacts',
    itemType: 'fieldarray',
    label: 'Internal Contacts',
    array: [
      {
        precondition: (values: any) =>
          !values?.[arrayName || 'contacts']?.[values.index]?.stakeholdertype,
        name: 'stakeholdertype',
        label: 'Type',
        itemType: 'autocomplete',
        options: sortedRoles,
      },
      ...(sortedRoles.map(role =>
        variableRoleStakeholder(
          role,
          internalRoleHolderOptions({
            subscribers,
            role,
          }),
          setOpenContact,
          arrayName || 'contacts'
        )
      ) || []),
    ],
  };
};

export const variableRoleStakeholder = (
  role: Role,
  options: any[],
  setOpenContact: Function,
  arrayName?: string,
  additionalProps?: any,
  additionalJItemProps?: any
) => {
  const roleStkhldr = roleStakeholder(
    'contact',
    role.name,
    role,
    options,
    setOpenContact,
    arrayName
  );
  // const base = baseStakeholder({ options, role });
  return {
    ...roleStkhldr,
    precondition: (values: any) =>
      values?.[arrayName || 'stakeholders']?.[values.index]?.stakeholdertype
        ?.id === role.id,
    jItemProps: {
      ...roleStkhldr.jItemProps,
      ...(additionalJItemProps || {}),
      onChange: (value: any, name: string, setFieldValue: Function) => {
        setFieldValue(name.replace('contact', 'role'), role);
      },
    },
    ...(additionalProps || {}),
  } as JItem;
};

export const roleStakeholder = (
  name: string,
  label: string,
  role: Role,
  options: any[],
  setOpenContact: Function,
  arrayName?: string
) => {
  const base = baseStakeholder({ options, role });
  return {
    ...base,
    name: name,
    label: label,
    jItemProps: {
      ...base.jItemProps,
      //REMOVE THE ABILITY TO ADD
      // addValue: async (
      //   name: any,
      //   newValue: any,
      //   values: any,
      //   formik: FormikHelpers<any>
      // ) => {
      //   const names = newValue?.inputValue?.value?.split(' ');
      //   formik.setFieldValue(name, undefined);
      //   setOpenContact({
      //     open: true,
      //     // type: newValue?.type,
      //     callback: (contact: SubscriptionContact) => {
      //       formik.setFieldValue(name, {
      //         ...contact?.contact_r,
      //         name: contact?.organization_r
      //           ? contact?.organization_r.name
      //           : `${contact?.contact_r?.firstname} ${
      //               contact?.contact_r?.lastname
      //             }${
      //               contact?.contact_r?.email
      //                 ? ` - ${contact?.contact_r?.email}`
      //                 : ''
      //             }`,
      //         role: role?.id,
      //         role_r: role,
      //         organization: contact?.organization_r,
      //         type: contact?.organization_r ? 'organization' : undefined,
      //       });
      //       formik.setFieldValue(name.replace('contact', 'role'), role);
      //     },
      //     initialValues: {
      //       role,
      //       firstname: names[0],
      //       lastname: name.length > 1 ? names[1] : '',
      //       name: newValue?.inputValue?.value,
      //       type: newValue?.inputValue?.type,
      //     },
      //   });
      // },
    },
  } as JItem;
};

export const customStakeholder = (
  options: any[],
  setOpenContact: Function,
  arrayName?: string,
  additionalProps?: any,
  additionalJItemProps?: any
) => {
  const base = baseStakeholder({ options });
  return {
    ...base,
    precondition: (values: any) =>
      values?.[arrayName || 'stakeholders']?.[values.index]?.stakeholdertype
        ?.id === -1, //Custom
    name: 'dynamic',
    label: 'Custom Field',
    jItemProps: {
      ...base.jItemProps,
      ...(additionalJItemProps || {}),
      type: 'custom',
      labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
        return (
          getIn(values, name)?.label || 'Please enter the stakeholder label...'
        );
      },
      //REMOVE THE ABILITY TO ADD
      // addValue: async (
      //   name: any,
      //   newValue: any,
      //   values: any,
      //   formik: FormikHelpers<any>
      // ) => {
      //   const names = newValue?.inputValue?.value?.split(' ');
      //   formik.setFieldValue(name, {
      //     ...getIn(values, name),
      //     value: '',
      //   });
      //   setOpenContact({
      //     open: true,
      //     callback: (contact: SubscriptionContact) => {
      //       formik.setFieldValue(name.replace('.dynamic', ''), {
      //         dynamic: {
      //           ...getIn(values, name),
      //           value: {
      //             ...contact?.contact_r,

      //             role: contact?.role_r?.name,
      //             organization: contact?.organization_r,
      //             name: contact?.organization_r
      //               ? contact?.organization_r.name
      //               : `${contact?.contact_r?.firstname} ${
      //                   contact?.contact_r?.lastname
      //                 }${
      //                   contact?.contact_r?.email
      //                     ? ` - ${contact?.contact_r?.email}`
      //                     : ''
      //                 }`,
      //             type: contact?.organization_r ? 'organization' : undefined,
      //           },
      //           stakeholdertype: {
      //             id: -1,
      //             name: 'Custom',
      //           },
      //         },
      //         form: {
      //           ...getIn(values, name),
      //           value: {
      //             ...contact?.contact_r,
      //             role: contact?.role_r?.name,
      //             name: contact?.organization_r
      //               ? contact?.organization_r.name
      //               : `${contact?.contact_r?.firstname} ${
      //                   contact?.contact_r?.lastname
      //                 }${
      //                   contact?.contact_r?.email
      //                     ? ` - ${contact?.contact_r?.email}`
      //                     : ''
      //                 }`,
      //             organization: contact?.organization_r,
      //             type: contact?.organization_r ? 'organization' : undefined,
      //           },
      //           stakeholdertype: {
      //             id: -1,
      //             name: 'Custom',
      //           },
      //         },
      //         stakeholdertype: {
      //           id: -1,
      //           name: 'Custom',
      //         },
      //         contact: {
      //           ...contact?.contact_r,
      //           name: `${contact?.contact_r?.firstname} ${
      //             contact?.contact_r?.lastname
      //           }${
      //             contact?.contact_r?.email
      //               ? ` - ${contact?.contact_r?.email}`
      //               : ''
      //           }`,
      //           role: contact?.role_r?.name,
      //           organization: contact?.organization,
      //         },
      //       });
      //     },
      //     initialValues: {
      //       firstname: names[0],
      //       lastname: name.length > 1 ? names[1] : '',
      //       name: newValue?.inputValue?.value,
      //     },
      //   });
      // },
    },
    ...(additionalProps || {}),
  };
};

export const baseStakeholder = ({
  options,
  role,
}: {
  options: any[];
  role?: Role;
}) =>
  ({
    // freeSolo: true,
    // freeSoloFormat: (value: any) => value,
    jItemProps: {
      getOptionLabel: (option: any) => {
        return typeof option === 'string'
          ? option
          : (option.type === 'organization' || !!option.organization_r) &&
            !option.contact &&
            !option.contact_r &&
            !option.firstname
          ? `Organization: ${option.name || option.organization_r?.name}`
          : option?.firstname || option?.contact_r?.firstname
          ? `${option?.firstname || option?.contact_r?.firstname} ${
              option?.lastname || option?.contact_r?.lastname
            }${
              option?.email || option?.contact_r?.email
                ? ` - ${option?.email || option?.contact_r?.email}`
                : ''
            }${
              option?.organization_r?.name
                ? ` - ${option?.organization_r?.name}`
                : ''
            }`
          : '';
      },
      optionsFilter: createFilterOptions({
        stringify: (option: any) =>
          `${option?.contact_r?.firstname} ${option?.contact_r?.lastname} ${option?.contact_r?.email} ${option?.organization_r?.name} ${option?.name}`,
      }),
      ...renderStakeholderContactGroups(role),
      filterOptions: (options: any[], params: any) => {
        const filtered: any[] = createFilterOptions({
          stringify: (option: any) =>
            `${option?.contact_r?.firstname} ${option?.contact_r?.lastname} ${option?.contact_r?.email} ${option?.organization_r?.name} ${option?.name}`,
        })(options, params);

        const { inputValue } = params;
        //REMOVING THE ABILITY TO ADD
        // // Suggest the creation of a new value
        // if (inputValue !== '' && (!filtered || filtered.length === 0)) {
        //   filtered.push({
        //     inputValue: { value: inputValue, type: 'individual' },
        //     title: `Add "${inputValue}" as individual contact`,
        //     addValue: true,
        //   });
        //   filtered.push({
        //     inputValue: { value: inputValue, type: 'organization' },
        //     title: `Add "${inputValue}" as organization`,
        //     addValue: true,
        //   });
        // }

        return filtered;
      },
      InputProps: {
        // inputComponent: CustomInputComponent,
        startAdornment: (
          name: any,
          values: any,
          formik: FormikHelpers<any>
        ) => {
          if (!getIn(values, name)) {
            return (
              <Tooltip title={`Reset type`}>
                <IconButton
                  onClick={() => {
                    formik.setFieldValue(
                      name.replaceAll('.contact', '.stakeholdertype'),
                      undefined
                    );
                  }}
                >
                  <RestartAlt color="secondary" />
                </IconButton>
              </Tooltip>
            );
          } else {
            return <div />;
          }
        },
      },
      renderDisabledAdornment: (value: any) => {
        // console.log('value', value, role);
        return (
          <AvatarsCell
            small={true}
            data={[{ ...value, role_r: role } as any]}
            tooltip={ContactTooltip}
          />
        );
      },
    },
    options: options,
    itemType: 'autocomplete',
  } as JItem);
